import chroma from "chroma-js";
import { productRecommendationBaseStyles } from "@zoovu/runner-web-design-base";
import { ResolvedComponentsConfiguration } from "@zoovu/runner-browser-api";
import globals from "../partials/global-variables";
import stylesConfig from "../styles.config";
import mediaQuery from "../abstract/media-query";

const productNameFontSize = stylesConfig.typography.fontSize(1.125);
const productNameFontColor = `${chroma(globals.colors.contrastDark).alpha(0.9)}`;
const productPriceFontColor = productNameFontColor;
const productPriceFontSize = stylesConfig.typography.fontSize(1.75);
const compareButtonFontColor = stylesConfig.colors.secondaryFontColor;
const productButtonBackground = {
  border: stylesConfig.colors.secondary.spectrum500,
  background: stylesConfig.colors.secondary.spectrum500,
  hover: stylesConfig.colors.secondary.spectrum800,
};
const productPropertiesFontColor = {
  default: stylesConfig.colors.secondaryAccentColorVariants.gray,
  hover: stylesConfig.colors.secondaryAccentColorVariants.gray,
  negative: stylesConfig.colors.secondaryAccentColorVariants.gray,
  positive: stylesConfig.colors.secondaryAccentColorVariants.gray,
};
const focusOutlineColor = stylesConfig.colors.secondary.spectrum400;

export const productButtonStyles = {
  backgroundColor: productButtonBackground.background,
  border: "1px solid",
  borderColor: productButtonBackground.border,
  borderRadius: 0,
  color: compareButtonFontColor,
  cursor: "pointer",
  display: "block",
  fontSize: stylesConfig.typography.fontSize(),
  fontWeight: 700,
  margin: "15px auto 0",
  order: 2,
  padding: "12px 10px",
  textAlign: "center",
  textDecoration: "none",
  textTransform: "uppercase",
  transition: "background .3s ease",
  width: "100%",
  [mediaQuery.hover]: {
    "&:hover": {
      backgroundColor: productButtonBackground.hover,
    },
  },
  [`@media (${globals.breakpoints.$xs})`]: {
    width: "70%",
  },
};

export default {
  container: {
    ...productRecommendationBaseStyles(globals),
    border: "1px solid",
    borderColor: stylesConfig.colors.secondary.spectrum100,
    "& .product-details": {
      display: "flex",
      flexFlow: "wrap column",
      height: "100%",
      padding: "18px",
      transition: "background-color .5s",
    },
    "& .product-image": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: "0 0 12px",
      height: "200px",
      maxWidth: "100%",
      "& a": {
        "&:focus": {
          outlineColor: stylesConfig.colors.primary.spectrum400,
        },
      },
      "& img": {
        maxWidth: "200px",
        height: "auto",
        width: "100%",
        maxHeight: "200px",
      },
    },
    "& a.product-name": {
      textDecoration: "none",
      fontWeight: 500,
      textAlign: "center",
      fontSize: productNameFontSize,
      color: productNameFontColor,
      fontFamily: stylesConfig.typography.fontFamily,
      lineHeight: "1.2em",
      width: "100%",
      display: "block",
      minHeight: "57px",
      "&:focus": {
        outlineColor: stylesConfig.colors.primary.spectrum400,
      },
      [`@media (${globals.breakpoints.$xs})`]: {
        minHeight: "unset",
      },
    },
    "& .product-compare-wrapper": {
      marginTop: "15px",
      order: 4,
      [`@media (${globals.breakpoints.$xs})`]: {
        display: "none",
      },
      "& label, & .start-compare-button": {
        fontSize: stylesConfig.typography.fontSize(0.875),
        fontWeight: 400,
        letterSpacing: ".5px",
        lineHeight: "1em",
        color: `${chroma(globals.colors.contrastDark).alpha(0.8)}`,
        display: "inline-block",
        "&:focus": {
          outlineColor: focusOutlineColor,
        },
      },
      "& .start-compare-button": {
        marginTop: "5px",
      },
    },
    "& .rating-wrapper": {
      margin: "10px 0",
      textAlign: "center",
      "& .rating-image": {
        background: (configuration: ResolvedComponentsConfiguration): string =>
          `${globals.customerSpriteTheme5(configuration)} no-repeat 0 -44px`,
        width: "83px",
        height: "14px",
        margin: "0 auto",
        "& .rating-image-over": {
          background: (configuration: ResolvedComponentsConfiguration): string =>
            `${globals.customerSpriteTheme5(configuration)} no-repeat 0 -25px`,
          height: "14px",
        },
      },
    },
    "& .properties-title": {
      fontSize: stylesConfig.typography.fontSize(1),
      fontWeight: 400,
      letterSpacing: ".1px",
      lineHeight: "1.2em",
      color: `${chroma(globals.colors.contrastDark).alpha(0.6)}`,
      margin: 0,
    },
    "& .product-properties": {
      order: 1,
      padding: 0,
      listStyle: "none",
      margin: ["10px", 0, 0, 0],
      overflow: "hidden",
      color: productPropertiesFontColor.default,
      wordWrap: "break-word",
      maxWidth: "100%",
      "& > li": {
        marginBottom: "3px",
        fontSize: stylesConfig.typography.fontSize(0.875),
      },
      "& i": {
        height: "15px",
        width: "15px",
        display: "inline-block",
        marginRight: "7px",
      },

      "& .positive-property": {
        color: productPropertiesFontColor.positive,
        "& i": {
          position: "relative",
          top: "3px",
          backgroundImage: (configuration: ResolvedComponentsConfiguration): string =>
            `${globals.sprite(configuration)}`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "2px -26px",
          backgroundSize: "229px 229px",
        },
      },
      "& .negative-property": {
        color: productPropertiesFontColor.negative,
        "& i": {
          position: "relative",
          top: "3px",
          backgroundImage: (configuration: ResolvedComponentsConfiguration): string =>
            `${globals.sprite(configuration)}`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "-13px -26px",
          backgroundSize: "229px 229px",
        },
      },
      "& .neutral-property": {
        color: productPropertiesFontColor.default,
        "& i": {
          position: "relative",
          top: "3px",
          backgroundImage: (configuration: ResolvedComponentsConfiguration): string =>
            `${globals.sprite(configuration)}`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "-28px -26px",
          backgroundSize: "229px 229px",
        },
      },
    },
    "& .product-attributes-toggle": {
      order: 2,
      display: "inline-block",
      width: "auto",
      fontSize: stylesConfig.typography.fontSize(0.875),
      fontFamily: stylesConfig.typography.fontFamily,
      color: stylesConfig.colors.primaryFontColor,
      fontWeight: 400,
      letterSpacing: ".25px",
      lineHeight: "1em",
      margin: "5px auto 0 0",
      padding: 0,
      border: "none",
      cursor: "pointer",
      backgroundColor: "transparent",
      "&:after": {
        content: "' '",
        display: "inline-block",
        backgroundImage: (configuration: ResolvedComponentsConfiguration): string => `${globals.sprite(configuration)}`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "-92px -43px",
        backgroundSize: "229px 229px",
        width: "12px",
        height: "7px",
        marginLeft: "5px",
      },
      "&.collapsed": {
        "&:after": {
          transform: "rotate(180deg)",
        },
      },
      "&:focus": {
        outlineColor: focusOutlineColor,
      },
    },
    "& .price-compare-wrapper": {
      order: 3,
      textAlign: "left",
      "& .product-price": {
        marginTop: "10px",
      },
      "& .price": {
        fontWeight: "400",
        color: productPriceFontColor,
        fontSize: productPriceFontSize,
        lineHeight: "1em",
        padding: ["10px", 0, 0],
      },
    },
    "& .product-footer": {
      display: "flex",
      flexFlow: "column",
      order: 5,
      position: "relative",
      marginTop: "auto",
    },
    "& .product-button": {
      ...productButtonStyles,
    },
  },
};
